import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Image as PhotoIcon } from "lucide-react";

export default function Signup() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    gender: "",
    bio: "",
    age: "",
    role: "",
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [missingDetailsError, setMissingDetailsError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      let score = 0;
      if (value.length > 5) score++;
      if (/[A-Z]/.test(value)) score++;
      if (/[0-9]/.test(value)) score++;
      if (/[^A-Za-z0-9]/.test(value)) score++;
      setPasswordStrength(score);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setProfilePicture(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMissingDetailsError("");

    const required = [
      "username",
      "password",
      "name",
      "email",
      "gender",
      "age",
      "role",
    ];
    const missing = required.filter((f) => !formData[f]);
    if (missing.length > 0) {
      setMissingDetailsError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      for (const key in formData) form.append(key, formData[key]);
      if (profilePicture) form.append("profilePicture", profilePicture);

      const res = await fetch("https://happyu.onrender.com/signup", {
        method: "POST",
        body: form,
      });
      if (res.status === 409) {
        const data = await res.json();
        setError(data.msg);
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error("Network error");
      await res.json();
      navigate("/login");
    } catch (err) {
      setError("Signup failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-r from-[#fff8e1] via-[#fdfaf3] to-[#fef9e7] px-6 py-12">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-3xl bg-[#fffdf7]/90 backdrop-blur-md p-8 rounded-2xl shadow-lg border border-[#f1e4c3]"
      >
        <h2 className="text-3xl font-bold text-center text-black mb-8">
          Create Your Account
        </h2>

        {error && <p className="text-center text-red-500 mb-4">{error}</p>}
        {missingDetailsError && (
          <p className="text-center text-red-500 mb-4">{missingDetailsError}</p>
        )}

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Username *
            </label>
            <input
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Password *
            </label>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            />
            {formData.password && (
              <div className="h-2 mt-2 bg-gray-200 rounded-full">
                <div
                  className={`h-full rounded-full transition-all ${
                    passwordStrength === 1
                      ? "bg-red-400 w-1/4"
                      : passwordStrength === 2
                      ? "bg-yellow-400 w-1/2"
                      : passwordStrength === 3
                      ? "bg-amber-500 w-3/4"
                      : passwordStrength === 4
                      ? "bg-green-600 w-full"
                      : ""
                  }`}
                ></div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Full Name *
            </label>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Gender *
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Non Binary</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Age *
            </label>
            <input
              name="age"
              value={formData.age}
              onChange={handleChange}
              type="number"
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#8b6b45]">
              Role *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            >
              <option value="">Select</option>
              <option value="patient">Patient</option>
              <option value="counsellor">Counsellor</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-[#8b6b45]">
              Bio
            </label>
            <textarea
              name="bio"
              rows={3}
              value={formData.bio}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border-[#e6d9b7] shadow-sm focus:ring-[#d1b97b] focus:border-[#d1b97b]"
            ></textarea>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-[#8b6b45]">
              Profile Picture
            </label>
            <div className="flex items-center gap-4 mt-2">
              <label
                htmlFor="file-upload"
                className="cursor-pointer bg-[#d1b97b] text-black px-4 py-2 rounded-md hover:bg-[#c5a95a] transition flex items-center gap-2"
              >
                <PhotoIcon className="w-4 h-4" /> Upload
              </label>
              <input
                id="file-upload"
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="w-16 h-16 rounded-full object-cover border border-[#d1b97b]"
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-[#d1b97b] text-black font-semibold px-6 py-3 rounded-lg hover:bg-[#c5a95a] transition shadow-md"
          >
            {loading ? (
              <Loader2 className="animate-spin h-5 w-5 " />
            ) : (
              "Sign Up"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
